<script>
    /** API payloads use `nme` for display name; `name` kept as fallback for tests/defaults. */
    export let organization = { nme: 'OTO Fertility' };
    export let isRenewal = false;

    /** Fertility orgs keep fertility-specific copy; gym, tactical, and other non-fertility orgs use generic wording. */
    $: isFertilityOrg = organization?.is_fertility_org === true;
    $: orgDisplayName = organization?.nme || organization?.name || 'OTO';
    $: welcomeKicker = isRenewal
        ? `Welcome back to ${orgDisplayName}`
        : `Welcome to ${orgDisplayName}`;
</script>

<div class="success-page">
    <div class="success-card">
        <div class="success-icon" aria-hidden="true">
            <div class="success-icon-ring">
                <div class="success-icon-mark">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M5 12.5l4.5 4.5L19 7" />
                    </svg>
                </div>
            </div>
        </div>

        <p class="kicker">{welcomeKicker}</p>

        {#if isRenewal}
            <h1>Your subscription has been renewed.</h1>
            <p class="lead">You're all set. Your {orgDisplayName} subscription is now active again.</p>
            <p>You can log back in to the OTO Life App and pick up right where you left off. If you have any questions, our team is here to help.</p>
            <p class="signoff">Sincerely,<br /><strong>The {orgDisplayName} Team</strong></p>
        {:else if isFertilityOrg}
            <h1>Congratulations on taking this proactive next step in your fertility journey.</h1>
            <p class="lead">Every fertility journey is unique, and we want you to know you're not navigating it alone. With OTO Fertility, you'll gain real-time insights into how your body is responding to physiological stress, uncover patterns that matter, and make smarter, more personalized decisions.</p>
            <p>You'll soon receive your OTO Fertility Biosensor in the mail, along with instructions to help you get started. You'll want to begin your assessments as soon as possible. Remember, the earlier you start, the faster you'll gain access to your customized activity plan.</p>
            <p>Your daily data will automatically sync with the OTO Life App you'll access on your mobile device. This is where you'll see your daily assessment data, <strong>Fertility Index</strong>, <strong>Stress Resilience Index</strong>, and personalized action plan. Be sure to follow along with your customized activity plan recommendations and then review your data with your clinician.</p>
            <p class="signoff">Sincerely,<br /><strong>The OTO Fertility Team</strong></p>
        {:else}
            <h1>Congratulations on taking this proactive next step on your journey with {orgDisplayName}.</h1>
            <p class="lead">Every journey is unique, and you are not navigating it alone. With OTO, you will gain real-time insights into how your body is responding to physiological stress, uncover patterns that matter, and make smarter, more personalized decisions.</p>
            <p>You will soon receive your OTO Biosensor in the mail, along with instructions to help you get started. Begin your assessments as soon as you can; the earlier you start, the faster you will gain access to your customized activity plan.</p>
            <p>Your daily data will automatically sync with the OTO Life App on your mobile device. There you will see your daily assessment data, <strong>Stress Resilience Index</strong>, and personalized action plan. Follow your customized activity plan and review your data with your coach or clinician.</p>
            <p class="signoff">Sincerely,<br /><strong>The {orgDisplayName} Team</strong></p>
        {/if}

        <div class="notice">
            If your confirmation email hasn't arrived within 5 minutes, check your spam or junk folder. Still nothing? We're at
            <a href="mailto:support@oto.coach">support@oto.coach</a>.
        </div>
    </div>
</div>

<style lang="scss">
    $accent: #2d80b1;
    $check: #12a37a;

    .success-page {
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        width: 100%;
        padding: 80px 20px 48px;
    }

    .success-card {
        position: relative;
        max-width: 720px;
        margin: 0 auto;
        background:
            radial-gradient(ellipse 90% 70% at 50% -8%, #e7f3fa 0%, #f7fbfd 38%, #ffffff 72%);
        border-radius: 24px;
        padding: 64px 48px 32px;
        box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
        text-align: left;
        color: #4b5563;
    }

    .success-icon {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, -50%);
        filter: drop-shadow(0 14px 24px rgba(18, 163, 122, 0.28));
    }

    .success-icon-ring {
        padding: 8px;
        background: rgba(255, 255, 255, 0.86);
        border: 1px solid rgba(255, 255, 255, 0.95);
        border-radius: 3dvh;
        box-shadow: 0 1px 0 rgba(255, 255, 255, 0.9) inset;
    }

    .success-icon-mark {
        width: 72px;
        height: 72px;
        border-radius: 24px;
        background: $check;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            width: 56px;
            height: 46px;
        }
    }

    .kicker {
        margin: 4px 0 16px;
        text-align: center;
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: $accent;
    }

    h1 {
        
        margin: 10px auto 40px;
        text-align: center;
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 30px;
        font-weight: 700;
        line-height: 1.22;
        letter-spacing: -0.03em;
        color: #111827;
    }

    p {
        margin: 0 0 16px;
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 16px;
        line-height: 1.65;
        color: #4b5563;
    }

    .lead {
        margin-bottom: 20px;
        padding-left: 16px;
        border-left: 3px solid $accent;
        color: #374151;
    }

    .signoff {
        margin: 8px 0 0;
        padding-top: 20px;
        border-top: 1px solid #eaecf0;
        color: #344054;

        strong {
            font-family: inherit;
            font-weight: 700;
            color: #101828;
        }
    }

    .notice {
        margin-top: 24px;
        padding: 14px 16px;
        background: #f8f9fb;
        border: 1px solid #eaecf0;
        border-radius: 10px;
        font-size: 13px;
        line-height: 1.5;
        color: #667085;

        a {
            color: $accent;
            text-decoration: none;
            font-weight: 600;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    @media screen and (max-width: 767px) {
        .success-page {
            width: 100%;
            max-width: 100%;
            padding: 48px 12px 28px;
            box-sizing: border-box;
        }

        .success-card {
            width: 100%;
            max-width: 100%;
            padding: 48px 16px 20px;
            border-radius: 16px;
            box-sizing: border-box;
        }

        h1 {
            font-size: 22px;
            margin: 8px 0 20px;
        }

        p {
            font-size: 15px;
        }

        .success-icon-mark {
            width: 52px;
            height: 52px;

            svg {
                width: 28px;
                height: 28px;
            }
        }
    }
</style>
